"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.wordpressUpdatePage = exports.wordpressUpdatePageResultSchema = exports.wordpressUpdatePageArgsSchema = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
// WordPress-specific metadata for content changes
var wordpressContentChangeSchema = common_1.contentChangeSchema.extend({
    changeReason: zod_1.z.string().nullish().describe('Explanation of why this change improves SEO'),
    impactLevel: zod_1.z.enum(['low', 'medium', 'high']).nullish().describe('SEO impact level'),
    keywordsTargeted: zod_1.z.array(zod_1.z.string()).nullish().describe('Keywords this change targets'),
});
// Section schemas (same as seoArticleWriter for compatibility)
var headerSectionSchema = zod_1.z.object({
    type: zod_1.z.literal('header'),
    level: zod_1.z.number().min(1).max(6),
    content: zod_1.z.string(),
    id: zod_1.z.string().nullish(),
    keywords: zod_1.z.array(zod_1.z.string()).nullish(),
});
var paragraphSectionSchema = zod_1.z.object({
    type: zod_1.z.literal('paragraph'),
    content: zod_1.z.string(),
    id: zod_1.z.string().nullish(),
});
var imageSectionSchema = zod_1.z.object({
    type: zod_1.z.literal('image'),
    src: zod_1.z.string().url(),
    alt: zod_1.z.string().max(125),
    title: zod_1.z.string().nullish(),
    caption: zod_1.z.string().nullish(),
    width: zod_1.z.number().min(1).nullish(),
    height: zod_1.z.number().min(1).nullish(),
    formats: zod_1.z
        .object({
        webp: zod_1.z.string().url().nullish(),
        avif: zod_1.z.string().url().nullish(),
    })
        .nullish(),
});
var listSectionSchema = zod_1.z.object({
    type: zod_1.z.literal('list'),
    style: zod_1.z.enum(['ordered', 'unordered']),
    items: zod_1.z.array(zod_1.z.string()),
});
var tableSectionSchema = zod_1.z.object({
    type: zod_1.z.literal('table'),
    caption: zod_1.z.string().nullish(),
    headers: zod_1.z.array(zod_1.z.string()),
    rows: zod_1.z.array(zod_1.z.array(zod_1.z.string())),
});
var faqSectionSchema = zod_1.z.object({
    type: zod_1.z.literal('faq'),
    items: zod_1.z.array(zod_1.z.object({
        question: zod_1.z.string(),
        answer: zod_1.z.string(),
        id: zod_1.z.string().nullish(),
    })),
});
var codeSectionSchema = zod_1.z.object({
    type: zod_1.z.literal('code'),
    language: zod_1.z.string().nullish(),
    content: zod_1.z.string(),
    caption: zod_1.z.string().nullish(),
});
var blockquoteSectionSchema = zod_1.z.object({
    type: zod_1.z.literal('blockquote'),
    content: zod_1.z.string(),
    citation: zod_1.z.string().nullish(),
    url: zod_1.z.string().url().nullish(),
});
var contentSectionSchema = zod_1.z.union([
    headerSectionSchema,
    paragraphSectionSchema,
    imageSectionSchema,
    listSectionSchema,
    tableSectionSchema,
    faqSectionSchema,
    codeSectionSchema,
    blockquoteSectionSchema,
]);
// Discriminated union for content update types (mutually exclusive)
var htmlContentUpdate = zod_1.z.object({
    updateType: zod_1.z.literal('html'),
    content: zod_1.z.string().describe('Replace entire page content with HTML'),
});
var markdownContentUpdate = zod_1.z.object({
    updateType: zod_1.z.literal('markdown'),
    content: zod_1.z
        .string()
        .describe('Replace entire page content with Markdown - will be converted to HTML'),
});
var sectionsContentUpdate = zod_1.z.object({
    updateType: zod_1.z.literal('sections'),
    sections: zod_1.z
        .array(contentSectionSchema)
        .min(1)
        .describe('Replace entire page content with structured sections (compatible with seoArticleWriter)'),
});
var changesContentUpdate = zod_1.z.object({
    updateType: zod_1.z.literal('changes'),
    changes: zod_1.z
        .array(wordpressContentChangeSchema)
        .min(1)
        .describe('Apply incremental changes to existing content (compatible with seoArticleUpdater)'),
});
var noContentUpdate = zod_1.z.object({
    updateType: zod_1.z.literal('none'),
});
// Argument schema with discriminated union for content updates
exports.wordpressUpdatePageArgsSchema = zod_1.z.object({
    pageUrl: zod_1.z.string().describe('The URL or ID of the WordPress page to update'),
    title: zod_1.z.string().nullish().describe('Optional new page title'),
    contentUpdate: zod_1.z
        .discriminatedUnion('updateType', [
        htmlContentUpdate,
        markdownContentUpdate,
        sectionsContentUpdate,
        changesContentUpdate,
        noContentUpdate,
    ])
        .nullish()
        .describe('How to update page content (mutually exclusive options)'),
    status: zod_1.z
        .enum(['publish', 'draft', 'pending', 'private'])
        .nullish()
        .describe('Optional new page status (publish, draft, pending, or private)'),
    slug: zod_1.z.string().nullish().describe('Optional new URL-friendly slug for the page'),
    excerpt: zod_1.z.string().nullish().describe('Optional new brief description of the page'),
    featuredMediaId: zod_1.z.number().nullish().describe('Optional WordPress media ID for featured image'),
    parentId: zod_1.z.number().nullish().describe('Optional parent page ID for hierarchical pages'),
    menuOrder: zod_1.z.number().nullish().describe('Optional menu order for the page'),
    commentStatus: zod_1.z
        .enum(['open', 'closed'])
        .nullish()
        .describe('Optional comment status (open or closed)'),
    pingStatus: zod_1.z
        .enum(['open', 'closed'])
        .nullish()
        .describe('Optional ping/trackback status (open or closed)'),
});
exports.wordpressUpdatePageResultSchema = zod_1.z.object({
    id: zod_1.z.number().describe('The WordPress page ID'),
    url: zod_1.z.string().describe('The public URL of the updated page'),
    status: zod_1.z.string().describe('The page status (publish, draft, pending, or private)'),
    title: zod_1.z.string().describe('The page title'),
    slug: zod_1.z.string().describe('The URL-friendly slug'),
    modified: zod_1.z.string().describe('The last modified date (ISO 8601 format)'),
});
// Tool definition
exports.wordpressUpdatePage = (0, common_1.createToolMetadata)({
    id: 'wordpressUpdatePage',
    displayName: 'WordPress Update Page',
    displayDescription: 'Update an existing WordPress page by URL',
    category: 'communication',
    color: common_1.BRAND_COLORS.WORDPRESS,
    backgroundColor: (0, common_1.createBgColor)(common_1.BRAND_COLORS.WORDPRESS),
    icon: 'wordpress',
    argsSchema: exports.wordpressUpdatePageArgsSchema,
}, exports.wordpressUpdatePageResultSchema);
